import {
  doc,
  onSnapshot,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  limit,
  orderBy,
  arrayUnion,
  Timestamp,
  deleteField,
  getFirestore,
  DocumentReference,
  getCountFromServer,
} from "firebase/firestore";
import { db, dbCentral } from "../config/FirebaseConfig.js"; // Import the Firebase setup
import { getOrgById } from "./OrgFunctions.js";
import { getUserById } from "./UserFunctions.js";
import { isValid } from "date-fns";

export const convertSecondsToFirestoreTimestamp = (seconds) => {
  // Firestore expects milliseconds, so convert seconds to milliseconds
  const milliseconds = seconds * 1000;

  // Create and return a Firestore Timestamp
  return Timestamp.fromMillis(milliseconds);
};

export async function getUserDataById(id) {
  if (!id) {
    console.error("User ID is missing");
    return null;
  }
  const docRef = doc(dbCentral, "user", id);
  const docSnapshot = await getDoc(docRef);
  if (docSnapshot.exists()) {
    return { id: id, ref: docRef, ...docSnapshot.data() }; // Return plain object with document reference and ID
  }
  return null; // Return null if no document exists
}
export async function getOrgDataById(id) {
  if (!id) {
    console.error("Organization ID is missing");
    return null;
  }
  const docRef = doc(dbCentral, "org", id);
  const docSnapshot = await getDoc(docRef);
  if (docSnapshot.exists()) {
    return { id: id, ref: docRef, ...docSnapshot.data() }; // Return plain object with document reference and ID
  }
  return null; // Return null if no document exists
}
export async function getWorkspaceDataById(id) {
  if (!id) {
    console.error("Workspace ID is missing");
    return null;
  }
  const docRef = doc(dbCentral, "workspace", id);
  const docSnapshot = await getDoc(docRef);
  if (docSnapshot.exists()) {
    return { id: id, ref: docRef, ...docSnapshot.data() }; // Return plain object with document reference and ID
  }
  return null; // Return null if no document exists
}

export async function getDocDataById(collection, id) {
  if (!collection || !id) {
    console.error("Collection or ID is missing");
    return null;
  }
  const docRef = doc(db, collection, id);
  const docSnapshot = await getDoc(checkDB(docRef));
  if (docSnapshot.exists()) {
    return { id: id, ref: docRef, ...docSnapshot.data() }; // Return plain object with document reference and ID
  }
  return null; // Return null if no document exists
}

export function checkDB(docRef) {
  if (!docRef) {
    console.error("Document reference is missing");
    return null;
  }
  if (!(docRef instanceof DocumentReference)) {
    console.error("Invalid document reference provided");
    return null;
  }
  // If the document reference is from the central database, rebuild it
  if (
    docRef.path.startsWith("user/") ||
    docRef.path.startsWith("org/") ||
    docRef.path.startsWith("workspace/")
  ) {
    // Rebuild the document reference to point to the central database
    return doc(dbCentral, docRef.path);
  }
  return docRef; // Return the original reference if it's not from the central database
}

export function buildRefFromPath(path) {
  if (!path) {
    console.error("Path is missing");
    return null;
  }
  const parts = path.split("/");
  if (parts.length < 2) {
    console.error("Invalid path format");
    return null;
  }
  const collection = parts[0];
  const id = parts[1];
  return checkDB(doc(db, collection, id));
}

export async function getDocDataByRef(docRef) {
  if (!docRef) {
    console.error("Document reference is missing");
    return null;
  }
  if (!(docRef instanceof DocumentReference)) {
    console.error("Invalid document reference provided");
    return null;
  }
  // if collection is user, org or workspace, raise an error
  docRef = checkDB(docRef);
  const docSnapshot = await getDoc(docRef);
  if (docSnapshot.exists()) {
    return { id: docSnapshot.id, ref: docRef, ...docSnapshot.data() }; // Return plain object with document reference and ID
  }
  return null; // Return null if no document exists
}

export function listenDocDataByRef(docRef, callback) {
  docRef = checkDB(docRef);

  const unsubscribe = onSnapshot(
    docRef,
    (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = { id: docSnapshot.id, ref: docRef, ...docSnapshot.data() };
        callback(data);
      } else {
      }
    },
    (error) => {
      console.error("Error fetching folder:", error);
    }
  );

  return unsubscribe;
}

export function listenDocDataByRefX(docRef, callback, unsubscribeRef) {
  docRef = checkDB(docRef);

  // Ensure we unsubscribe from the previous listener before creating a new one
  if (unsubscribeRef.current) {
    unsubscribeRef.current(); // Stop the old listener
  }

  // Set up a new listener
  unsubscribeRef.current = onSnapshot(
    docRef,
    (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = { id: docSnapshot.id, ref: docRef, ...docSnapshot.data() };
        callback(data);
      }
    },
    (error) => {
      console.error("Error fetching document:", error);
    }
  );

  // Return a cleanup function to stop listening when needed
  return () => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }
  };
}

export function listenDocDataById(collection, id, callback) {
  let docRef = doc(db, collection, id);
  docRef = checkDB(docRef);

  return listenDocDataByRef(docRef, callback);
}

export function listenDocDataByIdX(collection, id, callback, unsubscribeRef) {
  let docRef = doc(db, collection, id);
  docRef = checkDB(docRef);

  return listenDocDataByRefX(docRef, callback, unsubscribeRef);
}

export async function createDoc(collectionName, data, db_ = null) {
  // Create a reference to the collection
  const docRef = doc(collection(db_ || db, collectionName)); // Automatically generates a document ID
  // Write the document
  await setDoc(docRef, data);
  // Fetch the newly created document
  const docSnapshot = await getDoc(docRef);
  return { id: docSnapshot.id, ref: docRef, ...docSnapshot.data() }; // Return the document's data with its ID and reference
}

export async function deleteDocById(collection, id, _db = null) {
  const docRef = doc(_db || db, collection, id);
  await deleteDoc(docRef);
}

export function last_of_list(list) {
  return list[list.length - 1];
}

export async function deleteDocByRef(docRef) {
  await deleteDoc(docRef);
}

export async function getArrayDocRefByIDs(collection, ids) {
  if (!ids) {
    console.error("List of document IDs is missing");
    return [];
  }
  const docRefs = ids.map((id) => checkDB(getRefFromId(collection, id)));
  return docRefs;
}

export async function generateDocumentsXFromDocumentsXX(documentsXXRefs) {
  if (!documentsXXRefs) {
    console.error("List of documentXX references is missing");
    return [];
  }
  const documentsX = [];
  for (let docXXRef of documentsXXRefs) {
    docXXRef = checkDB(docXXRef); // Ensure the reference is valid
    const docXXData = await getDocDataByRef(docXXRef);
    if (docXXData) {
      const docXData = {
        name: docXXData.name,
        description: docXXData.description,
        custom: false,
        docXX_ref: docXXRef,
      };
      const docX = await createDoc("documentx", docXData);
      documentsX.push(docX);
    }
  }
  return documentsX;
}

export async function generateDocumentsXRefsFromDocumentsXX(documentsXXRefs) {
  if (!documentsXXRefs) {
    console.error("List of documentXX references is missing");
    return [];
  }
  const documentsXRefs = [];
  for (let docXXRef of documentsXXRefs) {
    docXXRef = checkDB(docXXRef); // Ensure the reference is valid
    const docXXData = await getDocDataByRef(docXXRef);
    if (docXXData) {
      const docXData = {
        name: docXXData.name,
        icon: docXXData.icon,
        infos: docXXData.infos,
        storage_path: null,
        state: "locked",
        documentxx: docXXRef,
      };
      const docX = await createDoc("documentx", docXData);
      documentsXRefs.push(docX.ref);
    }
  }
  return documentsXRefs;
}

export async function getArrayDocDataByRef(listdocRef) {
  if (listdocRef === null || listdocRef === undefined) {
    console.error("List of document references is missing");
    return [];
  }
  if (listdocRef?.length === 0) {
    return [];
  }

  try {
    // Use Promise.all to fetch all documents concurrently
    const docSnapshots = await Promise.all(
      listdocRef.map((docRef) => getDoc(checkDB(docRef)))
    );
    // Filter and map the results to include only existing documents
    const docs = docSnapshots
      .filter((docSnapshot) => docSnapshot.exists())
      .map((docSnapshot) => ({
        id: docSnapshot.id,
        ref: docSnapshot.ref,
        ...docSnapshot.data(),
      }));

    return docs;
  } catch (error) {
    console.error("Error fetching documents:", error);
    return [];
  }
}

export async function getArrayDocDataByID(collection, listdocID) {
  if (!listdocID) {
    console.error("List of document references is missing");
    return [];
  }
  if (listdocID?.length === 0) {
    return [];
  }
  let listdocRef = [];
  for (const docID of listdocID) {
    const docRef = doc(db, collection, docID);
    listdocRef.push(checkDB(docRef));
  }

  try {
    // Use Promise.all to fetch all documents concurrently
    const docSnapshots = await Promise.all(
      listdocRef.map((docRef) => getDoc(docRef))
    );
    // Filter and map the results to include only existing documents
    const docs = docSnapshots
      .filter((docSnapshot) => docSnapshot.exists())
      .map((docSnapshot) => ({
        id: docSnapshot.id,
        ref: docSnapshot.ref,
        ...docSnapshot.data(),
      }));

    return docs;
  } catch (error) {
    console.error("Error fetching documents:", error);
    return [];
  }
}

export function getFlutterTime() {
  return Timestamp.now();
}

export function getFlutterTimeFromSeconds(seconds) {
  return Timestamp.fromMillis(seconds * 1000);
}

export function getDateFromFlutter(flutter_date) {
  if (!flutter_date) {
    return null;
  }
  return new Date(flutter_date?.seconds * 1000);
}
export function getLocalStringFromFlutter(flutter_date, no_year = false) {
  if (!flutter_date) {
    return null;
  }
  let date = new Date(flutter_date?.seconds * 1000);
  if (date && isValid(date)) {
    return no_year
      ? date
          .toLocaleString("fr-FR", {
            year: "2-digit",
            month: "2-digit",
            day: "2-digit",
          })
          .replace(/\/\d{2}$/, "")
      : date.toLocaleString();
  }
  return null;
}

export function getDateFromFirebase(start) {
  return new Date(start.seconds * 1000);
}

export function getDateFromSeconds(seconds) {
  return new Date(seconds * 1000);
}
export function getFlutterTimeFromSecondsFormatDate(seconds) {
  // to ("DD/MM/YYYY")
  return getFlutterTimeFromSeconds(seconds)
    .toDate()
    .toLocaleDateString("fr-FR");
}

export function getFlutterTimeFromSecondsFormatTime(seconds) {
  // to ("HH:mm")
  return getFlutterTimeFromSeconds(seconds)
    .toDate()
    .toLocaleTimeString("fr-FR");
  // to ("DD/MM/YYYY")
}

export async function updateDocDataField(docRef, field, value) {
  await setDoc(checkDB(docRef), { [field]: value }, { merge: true });
}

export async function deleteDocDataField(docRef, field) {
  await setDoc(checkDB(docRef), { [field]: deleteField() }, { merge: true });
}

export async function addToArrayField(docRef, field, item) {
  await setDoc(
    docRef,
    { [field]: arrayUnion(item) }, // Use arrayUnion to add item to array
    { merge: true } // Ensure existing data is not overwritten
  );
}

export function getRefFromId(collection, id, _db = null) {
  if (!collection || !id) {
    console.error("Collection or ID is missing");
    return null;
  }
  return checkDB(doc(_db || db, collection, id));
}

export async function getDocumentsByFolderRef(folderRef) {
  const docSnapshot = await getDoc(folderRef);
  const docData = docSnapshot.data();
  const docs = [];

  for (const docRef of docData?.documents || []) {
    const docSnapshot = await getDoc(docRef);
    if (docSnapshot.exists()) {
      docs.push({
        id: docRef.id,
        ref: docRef,
        ...docSnapshot.data(),
      }); // Include document reference and ID
    }
  }
  return docs || [];
}

export function TruncatedString(text, maxLength = 20) {
  const truncatedText =
    text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  return truncatedText;
}

// Dynamic query function
export async function queryCollection(
  collectionName,
  filters,
  limite = 10000,
  sortingField = null,
  sortingOrder = "asc",
  _db = null
) {
  if (!collectionName) {
    console.error("Collection name is missing");
    return [];
  }
  let objectsQuery;
  // Initialize query with the collection reference
  if (
    collectionName === "user" ||
    collectionName === "org" ||
    collectionName === "workspace"
  ) {
    objectsQuery = collection(dbCentral, collectionName);
  } else {
    objectsQuery = collection(_db || db, collectionName);
  }

  // Apply dynamic filters if provided
  if (filters && filters.length > 0) {
    const queries = filters.map((filter) =>
      where(filter.field, filter.operator, filter.value)
    );
    objectsQuery = query(objectsQuery, ...queries);
  }

  // Apply sorting if a sortingField is provided
  if (sortingField) {
    objectsQuery = query(objectsQuery, orderBy(sortingField, sortingOrder));
  }

  // Apply the limit to the query
  objectsQuery = query(objectsQuery, limit(limite));

  try {
    // Fetch the documents that match the query
    const querySnapshot = await getDocs(objectsQuery);

    // Map the documents to an array of data with document ID and reference
    const objects = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ref: doc.ref,
      ...doc.data(),
    }));

    return objects || [];
  } catch (error) {
    console.error("Error querying collection:", error);
    return [];
  }
}

// Dynamic count function
export async function countCollection(
  collectionName,
  filters = [],
  _db = null
) {
  if (!collectionName) {
    console.error("Collection name is missing");
    return 0;
  }

  let objectsQuery;

  // Select the right database
  if (
    collectionName === "user" ||
    collectionName === "org" ||
    collectionName === "workspace"
  ) {
    objectsQuery = collection(dbCentral, collectionName);
  } else {
    objectsQuery = collection(_db || db, collectionName);
  }

  // Apply filters if provided
  if (filters && filters.length > 0) {
    const queries = filters.map((filter) =>
      where(filter.field, filter.operator, filter.value)
    );
    objectsQuery = query(objectsQuery, ...queries);
  }

  try {
    const snapshot = await getCountFromServer(objectsQuery);
    return snapshot.data().count || 0;
  } catch (error) {
    console.error("Error counting collection:", error);
    return 0;
  }
}
export function listenCollection(
  collectionName,
  filters,
  limite = 1000,
  sortingField = null,
  sortingOrder = "asc",
  onDataChange,
  unsubscribeRef = null,
  onError
) {
  if (!collectionName) {
    console.error("Collection name is missing");
    return null;
  }

  // Unsubscribe from previous listener
  if (unsubscribeRef?.current) {
    unsubscribeRef.current();
  }

  // Get the correct database reference
  const dbRef =
    collectionName === "user" ||
    collectionName === "org" ||
    collectionName === "workspace"
      ? dbCentral
      : db;

  // Build all query constraints at once
  const queryConstraints = [];

  // Add filters
  if (filters && filters.length > 0) {
    filters.forEach((filter) => {
      queryConstraints.push(where(filter.field, filter.operator, filter.value));
    });
  }

  // Add sorting
  if (sortingField) {
    queryConstraints.push(orderBy(sortingField, sortingOrder));
  }

  // Add limit
  queryConstraints.push(limit(limite));

  try {
    // Build the complete query
    const objectsQuery = query(
      collection(dbRef, collectionName),
      ...queryConstraints
    );

    // Set up the listener
    const unsubscribe = onSnapshot(
      objectsQuery,
      (querySnapshot) => {
        const objects = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ref: doc.ref,
          ...doc.data(),
        }));

        if (onDataChange) {
          onDataChange(objects);
        }
      },
      (error) => {
        console.error("Error listening to collection:", error);
        if (onError) {
          onError(error);
        }
      }
    );

    // Store the unsubscribe function
    if (unsubscribeRef) {
      unsubscribeRef.current = unsubscribe;
    }

    return unsubscribe;
  } catch (error) {
    console.error("Error setting up snapshot listener:", error);
    if (onError) {
      onError(error);
    }
    return null;
  }
}
